import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "grc_cart_v1";

// Shipping rules (centralised so backend and UI stay in sync via env or constant)
export const SHIPPING_FREE_THRESHOLD_CENTS = 6000; // 60,00 €
export const STANDARD_SHIPPING_CENTS = 595; // 5,95 €

const CartCtx = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(next[idx].quantity + qty, 99) };
        return next;
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          unit: product.unit,
          price_cents: product.price_cents,
          image: product.image,
          quantity: Math.max(1, qty),
        },
      ];
    });
  }, []);

  const updateQty = useCallback((id, quantity) => {
    setItems((prev) => {
      if (quantity <= 0) return prev.filter((p) => p.id !== id);
      return prev.map((p) =>
        p.id === id ? { ...p, quantity: Math.min(Math.max(1, quantity), 99) } : p,
      );
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((acc, p) => acc + p.quantity, 0), [items]);
  const subtotalCents = useMemo(
    () => items.reduce((acc, p) => acc + p.price_cents * p.quantity, 0),
    [items],
  );
  const shippingCents = useMemo(() => {
    if (count === 0) return 0;
    return subtotalCents >= SHIPPING_FREE_THRESHOLD_CENTS ? 0 : STANDARD_SHIPPING_CENTS;
  }, [subtotalCents, count]);
  const totalCents = useMemo(
    () => subtotalCents + shippingCents,
    [subtotalCents, shippingCents],
  );
  const remainingForFreeCents = useMemo(
    () => Math.max(SHIPPING_FREE_THRESHOLD_CENTS - subtotalCents, 0),
    [subtotalCents],
  );
  const freeShippingProgress = useMemo(
    () =>
      Math.min(
        100,
        Math.round((subtotalCents / SHIPPING_FREE_THRESHOLD_CENTS) * 100),
      ),
    [subtotalCents],
  );

  const value = useMemo(
    () => ({
      items,
      count,
      subtotalCents,
      shippingCents,
      totalCents,
      remainingForFreeCents,
      freeShippingProgress,
      freeShippingThresholdCents: SHIPPING_FREE_THRESHOLD_CENTS,
      open,
      openCart: () => setOpen(true),
      closeCart: () => setOpen(false),
      addItem,
      updateQty,
      removeItem,
      clear,
    }),
    [
      items,
      count,
      subtotalCents,
      shippingCents,
      totalCents,
      remainingForFreeCents,
      freeShippingProgress,
      open,
      addItem,
      updateQty,
      removeItem,
      clear,
    ],
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};

export const formatEUR = (cents) =>
  (cents / 100).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
